import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, ArrowRight, Check, Loader2, CheckCircle2, X,
  User, Mail, Lock, Store, MapPin, Tag, AlertCircle,
} from 'lucide-react';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { useAppConfig } from '../context/AppConfigContext';

import { getBackendUrl } from '../services/origin';
const API = `${getBackendUrl()}/api`;


function PackageCard({ pkg, selected, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={`pkg-${pkg.slug}`}
      className={`relative text-left p-5 rounded-2xl border-2 transition-all ${
        selected ? 'border-[#D44E20] bg-[#FFF5F0] shadow-md' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}>
      {pkg.is_popular && (
        <span className="absolute -top-2.5 right-4 bg-[#D44E20] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          Più scelto
        </span>
      )}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-[#1A1A1A]">{pkg.name}</h3>
        {selected && <CheckCircle2 size={18} className="text-[#D44E20]" />}
      </div>
      <p className="text-xs text-slate-500 mb-3 min-h-[32px]">{pkg.description || ''}</p>
      <div className="mb-3">
        <span className="text-3xl font-bold text-[#1A1A1A]">{pkg.price_monthly}€</span>
        <span className="text-xs text-slate-500 ml-1">/mese</span>
      </div>
      {pkg.features && pkg.features.length > 0 && (
        <ul className="space-y-1 text-xs text-slate-600">
          {pkg.features.slice(0, 4).map((f, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <Check size={11} className="text-emerald-500 flex-shrink-0 mt-0.5" /> <span>{typeof f === 'string' ? f : f.name || ''}</span>
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}


export default function BrandSignup() {
  const navigate = useNavigate();
  const appCfg = useAppConfig();
  const [sp] = useSearchParams();
  const refFromUrl = (sp.get('ref') || '').toUpperCase();
  const googleEmail = (sp.get('email') || '').toLowerCase();
  const googleName = sp.get('name') || '';
  const googleSessionId = sp.get('google_session_id') || '';
  const isGoogleFlow = !!googleSessionId;

  // Single-tenant deploy: redirect to admin login
  useEffect(() => {
    if (appCfg.loaded && appCfg.single_tenant) navigate('/admin/login', { replace: true });
  }, [appCfg.loaded, appCfg.single_tenant, navigate]);

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [referralAgent, setReferralAgent] = useState(null); // {name, code} if valid
  const [referralChecking, setReferralChecking] = useState(false);

  const [form, setForm] = useState({
    name: '',
    venue_name: '',
    venue_address: '',
    admin_name: googleName,
    admin_email: googleEmail,
    admin_password: '',
    admin_password2: '',
    package_slug: '',
    referral_code: refFromUrl,
    accept_terms: false,
  });

  // Load packages
  useEffect(() => {
    axios.get(`${API}/public/packages`).then(r => {
      setPackages(r.data.packages || []);
      // Pre-select most popular or first
      const popular = r.data.packages?.find(p => p.is_popular) || r.data.packages?.[1] || r.data.packages?.[0];
      if (popular) setForm(f => ({ ...f, package_slug: popular.slug }));
    }).finally(() => setLoading(false));
  }, []);

  // Validate referral code (with debounce)
  useEffect(() => {
    const code = (form.referral_code || '').trim().toUpperCase();
    if (!code) {
      setReferralAgent(null);
      return;
    }
    setReferralChecking(true);
    const timer = setTimeout(() => {
      axios.get(`${API}/public/agents/referral/${code}`)
        .then(r => setReferralAgent(r.data.valid ? { name: r.data.agent_name, code: r.data.referral_code } : null))
        .catch(() => setReferralAgent(null))
        .finally(() => setReferralChecking(false));
    }, 400);
    return () => { clearTimeout(timer); setReferralChecking(false); };
  }, [form.referral_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isGoogleFlow) {
      if (form.admin_password !== form.admin_password2) {
        toast.error('Le password non coincidono'); return;
      }
      if (form.admin_password.length < 6) {
        toast.error('Password troppo corta (min 6 caratteri)'); return;
      }
    }
    if (!form.accept_terms) {
      toast.error('Devi accettare i termini'); return;
    }
    setSubmitting(true);
    try {
      const { admin_password2, ...rest } = form;
      const payload = isGoogleFlow
        ? { ...rest, admin_password: '', google_session_id: googleSessionId }
        : rest;
      const r = await axios.post(`${API}/public/brand-signup`, payload);
      toast.success(`Registrazione riuscita! Benvenuto ${r.data.user.name}`);
      // Auto-login with the same keys used by AuthContext
      localStorage.setItem('admin_token', r.data.token);
      localStorage.setItem('admin_user', JSON.stringify(r.data.user));
      // Redirect: if plan selected → plans page for checkout; else → admin dashboard
      if (r.data.next_step === 'checkout') {
        setTimeout(() => { window.location.href = '/admin/plans'; }, 600);
      } else {
        setTimeout(() => { window.location.href = '/admin'; }, 600);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la registrazione');
    }
    setSubmitting(false);
  };

  const fieldWrap = (icon, child) => (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</div>
      {child}
    </div>
  );
  const input = (name, type, placeholder, required = true, icon = null) => fieldWrap(icon,
    <input
      type={type} required={required} value={form[name]}
      onChange={e => setForm({ ...form, [name]: e.target.value })}
      placeholder={placeholder} data-testid={`signup-${name}`}
      className={`w-full ${icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20] focus:ring-2 focus:ring-[#D44E20]/10`}
    />);

  if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#D44E20]" size={32} /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#FFF5F0] py-10 px-4" data-testid="brand-signup-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D44E20] to-[#F59E0B] flex items-center justify-center shadow-lg">
          <Zap size={22} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#1A1A1A]">Lazo</h1>
          <p className="text-xs text-slate-500">Multi-Brand Loyalty SaaS</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: hero + referral banner */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-6">
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">Crea il tuo account Lazo</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              Attiva la tua loyalty suite completa in 2 minuti.
              Punti, premi, eventi, shop online e comunicazioni multi-canale — tutto in un solo strumento.
            </p>

            <ul className="space-y-2 text-sm mb-5">
              {[
                'Setup guidato in 5 minuti',
                'Nessuna carta di credito per iniziare',
                'Cancella quando vuoi',
                'Supporto in italiano',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <Check size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>

            {/* Referral banner */}
            {referralAgent && (
              <div className="bg-gradient-to-br from-[#D44E20] to-[#b33d16] text-white rounded-xl p-4 mb-2" data-testid="referral-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Tag size={14} />
                  <p className="text-[10px] font-bold uppercase tracking-wider">Segnalato da</p>
                </div>
                <p className="text-base font-bold">{referralAgent.name}</p>
                <p className="text-[11px] text-white/80 mt-1 font-mono">Codice: {referralAgent.code}</p>
              </div>
            )}

            <p className="text-[11px] text-slate-400 mt-4">
              Hai già un account? <a href="/admin/login" className="text-[#D44E20] font-semibold hover:underline">Accedi</a>
            </p>

            {!isGoogleFlow && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Registrazione rapida</p>
                <GoogleLoginButton flow="signup" testId="signup-google-btn" />
                <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                  Con Google saltiamo email e password — poi scegli piano e attività.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right: form */}
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-6">
          {/* Package selector */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-[#1A1A1A]">1. Scegli il tuo piano</h3>
              <span className="text-[11px] text-slate-500">Potrai cambiarlo quando vuoi</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {packages.map(p => (
                <PackageCard key={p.slug} pkg={p} selected={form.package_slug === p.slug}
                  onClick={() => setForm({ ...form, package_slug: p.slug })} />
              ))}
            </div>
          </div>

          {/* Brand info */}
          <div>
            <h3 className="text-base font-bold text-[#1A1A1A] mb-3">2. La tua attività</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Nome brand*</label>
                {input('name', 'text', 'Es. Taco Loco', true, <Store size={14} />)}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Nome sede (opz.)</label>
                {input('venue_name', 'text', 'Es. Via Roma', false, <MapPin size={14} />)}
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Indirizzo sede (opz.)</label>
                {input('venue_address', 'text', 'Via Roma 1, Roma', false, <MapPin size={14} />)}
              </div>
            </div>
          </div>

          {/* Admin info */}
          <div>
            <h3 className="text-base font-bold text-[#1A1A1A] mb-3">3. Il tuo account amministratore</h3>
            {isGoogleFlow && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="google-verified-banner">
                <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                <span>Email verificata con Google: <b>{form.admin_email}</b> — non serve scegliere una password.</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Nome completo*</label>
                {input('admin_name', 'text', 'Mario Rossi', true, <User size={14} />)}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email*</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Mail size={14} /></div>
                  <input
                    type="email" required value={form.admin_email}
                    onChange={e => !isGoogleFlow && setForm({ ...form, admin_email: e.target.value })}
                    disabled={isGoogleFlow}
                    placeholder="mario@brand.it" data-testid="signup-admin_email"
                    className={`w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20] focus:ring-2 focus:ring-[#D44E20]/10 ${isGoogleFlow ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
              {!isGoogleFlow && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Password*</label>
                    {input('admin_password', 'password', 'min 6 caratteri', true, <Lock size={14} />)}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Ripeti password*</label>
                    {input('admin_password2', 'password', 'min 6 caratteri', true, <Lock size={14} />)}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Referral code */}
          <div>
            <h3 className="text-base font-bold text-[#1A1A1A] mb-3">4. Codice referral (opzionale)</h3>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Tag size={14} /></div>
              <input
                type="text"
                value={form.referral_code}
                onChange={e => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                placeholder="Inseriscilo se sei stato segnalato da un agente"
                data-testid="signup-referral-code"
                className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm uppercase font-mono focus:outline-none focus:ring-2 ${
                  referralAgent
                    ? 'border-emerald-300 bg-emerald-50 focus:border-emerald-500 focus:ring-emerald-500/10'
                    : form.referral_code
                      ? 'border-amber-300 bg-amber-50 focus:border-amber-500 focus:ring-amber-500/10'
                      : 'border-slate-200 focus:border-[#D44E20] focus:ring-[#D44E20]/10'
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {referralChecking && <Loader2 size={14} className="animate-spin text-slate-400" />}
                {!referralChecking && referralAgent && <CheckCircle2 size={16} className="text-emerald-500" />}
                {!referralChecking && !referralAgent && form.referral_code && <X size={16} className="text-amber-500" />}
              </div>
            </div>
            {referralAgent && (
              <p className="text-[11px] text-emerald-700 mt-1.5 font-semibold flex items-center gap-1">
                <Check size={11} /> Codice valido — sarai segnalato da <b>{referralAgent.name}</b>
              </p>
            )}
            {!referralAgent && !referralChecking && form.referral_code && (
              <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1">
                <AlertCircle size={11} /> Codice non valido o agente non attivo
              </p>
            )}
            {!form.referral_code && (
              <p className="text-[11px] text-slate-400 mt-1.5">Se hai ricevuto un link con <code>?ref=...</code>, il codice viene compilato in automatico.</p>
            )}
          </div>

          {/* Terms */}
          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.accept_terms}
                onChange={e => setForm({ ...form, accept_terms: e.target.checked })}
                data-testid="signup-accept-terms"
                className="mt-0.5 w-4 h-4 accent-[#D44E20]"
              />
              <span className="text-xs text-slate-600">
                Accetto i <a href="#" className="text-[#D44E20] font-semibold hover:underline">Termini di servizio</a> e l'<a href="#" className="text-[#D44E20] font-semibold hover:underline">Informativa privacy</a> di Lazo.
              </span>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || (form.referral_code && !referralAgent)}
            data-testid="signup-submit"
            className="w-full py-3.5 bg-[#D44E20] text-white font-bold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Crea account e procedi al pagamento <ArrowRight size={16} /></>}
          </button>
          <p className="text-center text-[10px] text-slate-400">
            Dopo la registrazione verrai reindirizzato alla pagina di pagamento sicuro tramite Stripe o PayPal.
          </p>
        </form>
      </div>
    </div>
  );
}
